import { useEffect, useRef, type PropsWithChildren } from 'react';
import { I18nProvider, useI18n } from '@ah/i18n';
import { messages } from '../../i18n';
import { useAuthStore } from '../../features/auth/auth.store';
import { useAccountApi } from '../../features/settings/useAccountApi';

/** Per-device storage key (ADR-015 §2) — distinct per app so the three frontends never collide in a shared browser. */
const STORAGE_KEY = 'ah.admin.locale';

/**
 * Wires @ah/i18n into the admin dashboard: persists the operator's language
 * choice locally and, once signed in, follows the account's server-side
 * `preferredLocale` and pushes changes back to it (ADR-015 §2/§3). Mirrors
 * apps/mobile and apps/therapist-portal's AppI18nProvider.
 */
export function AppI18nProvider({ children }: PropsWithChildren) {
  const accountApi = useAccountApi();
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);

  return (
    <I18nProvider
      messages={messages}
      storageKey={STORAGE_KEY}
      onLocaleChange={(locale) => {
        if (accessToken) void accountApi.updateLocale(accessToken, locale).catch(() => undefined);
      }}
    >
      <LocaleSync>{children}</LocaleSync>
    </I18nProvider>
  );
}

/** Syncs the active locale to the signed-in admin's saved preference once, after login resolves — never fights a mid-session manual switch. */
function LocaleSync({ children }: PropsWithChildren) {
  const user = useAuthStore((s) => s.user);
  const { syncLocale } = useI18n();
  const syncedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      syncedFor.current = null;
      return;
    }
    if (syncedFor.current === user.id) return;
    syncedFor.current = user.id;
    syncLocale(user.preferredLocale);
  }, [user, syncLocale]);

  return <>{children}</>;
}
